import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Ticket, Award, Crown, Gem, Sparkles, AlertCircle, Star } from 'lucide-react';
export const TicketsSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const ticketOptions = [{
    title: 'Darbar Pass',
    subtitle: 'Standing',
    price: '$500',
    features: ['Standing-room access to immersive experience', 'Late entry (after dinner service)', 'No seated dinner included', 'Access to dessert & chai lounge', 'Photo moments throughout the venue', 'Digital souvenir collection'],
    limitations: ['Standing-room only', 'No dinner service'],
    popular: false,
    icon: <Ticket className="w-8 h-8" data-id="element-674" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }, {
    title: 'Council',
    subtitle: 'Full Experience',
    price: '$1,000',
    features: ['Admission to immersive experience', 'Multi-course Punjabi dinner', 'Digital souvenir collection', 'Guided historical journey', 'VIP culinary experience', 'Behind-the-scenes tour'],
    popular: true,
    icon: <Award className="w-8 h-8" data-id="element-675" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }, {
    title: 'Founder Circle',
    subtitle: 'Limited Availability',
    price: '$2,500',
    features: ['All Council features', <span key="turban" data-id="element-676">
          <strong data-id="element-677">Personalized royal turban experience</strong> — wear an
          authentic turban designed and inspired by the Sikh Kingdom era
        </span>, <span key="coin" data-id="element-678">
          <strong data-id="element-679">Exclusive Maharaja Ranjit Singh Coin</strong>
        </span>, <span key="book" data-id="element-680">
          <strong data-id="element-681">Autographed pre-release copy of House of Lions</strong> —
          personally signed by author A.K. Singh
        </span>, 'Early prototype invite', 'Recognition as Founding Member', 'Private meet with creators', 'Custom historical portrait'],
    popular: false,
    icon: <Crown className="w-8 h-8" data-id="element-682" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }, {
    title: 'Patron Circle',
    subtitle: 'Table for 10',
    price: '$10,000',
    features: ['All Founder Circle features', 'Private hosted table for 10 guests', <span key="likeness-patron" data-id="element-683">
          <strong data-id="element-684">Your likeness featured in the exhibit</strong>
        </span>, 'Lifetime recognition as a major supporter', 'Exclusive pre-event reception', 'Input into future cultural experiences'],
    popular: false,
    icon: <Gem className="w-8 h-8" data-id="element-685" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }];
  return <motion.section id="tickets" ref={sectionRef} className="py-20 md:py-28 bg-black relative overflow-hidden" style={{
    opacity
  }} data-id="element-686">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-687"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-688">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-689">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-690">
            Reserve Your Place
          </p>
          <h2 className="font-serif text-5xl md:text-7xl text-white mb-8" data-id="element-691">
            TICKET
            <br data-id="element-692" />
            <span className="text-amber-400" data-id="element-693">TIERS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-694"></div>
          <div className="max-w-3xl mx-auto" data-id="element-695">
            <p className="text-xl text-amber-400 font-light mb-4" data-id="element-696">
              Prototype Event: April 25-26, 2026
            </p>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-697">
              Your ticket includes admission to the exclusive prototype event
              plus one monthly experience of your choice between May and
              December 2026
            </p>
          </div>
        </motion.div>
        {/* Tickets Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto mb-20" data-id="element-698">
          {ticketOptions.map((ticket, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: index * 0.1
        }} className={`relative ${ticket.popular ? 'lg:-mt-4 lg:mb-4' : ''}`} data-id="element-699">
              {/* Popular Badge */}
              {ticket.popular && <div className="absolute top-0 left-0 right-0 flex justify-center z-30 transform -translate-y-1/2" data-id="element-700">
                  <div className="bg-amber-400 text-black font-bold py-2 px-6 flex items-center gap-2 border-2 border-amber-300" data-id="element-701">
                    <Star className="w-4 h-4" fill="currentColor" data-id="element-702" />
                    <span className="text-xs uppercase tracking-wider" data-id="element-703">
                      Most Popular
                    </span>
                  </div>
                </div>}
              {/* Card */}
              <div className="border-2 border-amber-400/30 bg-black/80 h-full flex flex-col" data-id="element-704">
                <div className={`p-8 flex flex-col h-full ${ticket.popular ? 'pt-12' : ''}`} data-id="element-705">
                  {/* Icon & Title */}
                  <div className="text-center mb-8" data-id="element-706">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center border border-amber-400/30 bg-black" data-id="element-707">
                      <div className="text-amber-400" data-id="element-708">{ticket.icon}</div>
                    </div>
                    <h3 className="font-serif text-2xl text-white mb-2" data-id="element-709">
                      {ticket.title}
                    </h3>
                    {ticket.subtitle && <p className="text-amber-400/60 text-sm font-light tracking-wider uppercase" data-id="element-710">
                        {ticket.subtitle}
                      </p>}
                    <div className="text-3xl font-serif text-amber-400 mt-4" data-id="element-711">
                      {ticket.price}
                    </div>
                    <div className="w-16 h-px bg-amber-400/30 mx-auto mt-4" data-id="element-712"></div>
                  </div>
                  {/* Features */}
                  <ul className="mb-8 flex-grow space-y-3" data-id="element-713">
                    {ticket.features.map((feature, i) => <li key={i} className="text-white/70 text-sm flex items-start font-light" data-id="element-714">
                        <span className="flex-shrink-0 mr-3 mt-1.5" data-id="element-715">
                          <span className="block w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(251,191,36,0.5)]" data-id="element-716"></span>
                        </span>
                        <span data-id="element-717">{feature}</span>
                      </li>)}
                  </ul>
                  {/* Limitations */}
                  {ticket.limitations && <div className="mb-6 border border-amber-400/20 p-4 bg-black/40" data-id="element-718">
                      <div className="flex items-center mb-2" data-id="element-719">
                        <AlertCircle className="w-4 h-4 text-amber-400 mr-2" data-id="element-720" />
                        <span className="text-amber-400 text-xs uppercase tracking-wider" data-id="element-721">
                          Limitations
                        </span>
                      </div>
                      <ul className="space-y-1" data-id="element-722">
                        {ticket.limitations.map((limitation, i) => <li key={i} className="text-white/50 text-xs flex items-start font-light" data-id="element-723">
                            <span className="mr-2" data-id="element-724">•</span>
                            <span data-id="element-725">{limitation}</span>
                          </li>)}
                      </ul>
                    </div>}
                  {/* CTA Button */}
                  <motion.a href={ticket.link} target="_blank" rel="noopener noreferrer" className={`w-full py-3 text-center transition-colors duration-300 ${ticket.popular ? 'bg-amber-400 hover:bg-amber-500 text-black font-medium' : 'border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white'}`} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} data-id="element-726">
                    <span className="flex items-center justify-center gap-2 text-sm tracking-wider uppercase" data-id="element-727">
                      {ticket.popular && <Sparkles className="w-4 h-4" data-id="element-728" />}
                      Buy Tickets Now
                    </span>
                  </motion.a>
                </div>
              </div>
            </motion.div>)}
        </div>
        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center max-w-4xl mx-auto" data-id="element-729">
          <h3 className="font-serif text-3xl md:text-4xl text-white mb-4" data-id="element-730">
            History will only be reborn{' '}
            <span className="text-amber-400" data-id="element-731">once</span>. Will you be there?
          </h3>
          <p className="text-xl text-white/70 mb-8 font-light" data-id="element-732">
            Tickets are selling fast. Reserve your place in history today.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center" data-id="element-733">
            <motion.a href="https://www.tickettailor.com/events/sikhkingdoms/1934316" target="_blank" rel="noopener noreferrer" className="bg-amber-400 hover:bg-amber-500 text-black px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-734">
              Buy Tickets Now
            </motion.a>
            <motion.a href="https://www.tickettailor.com/events/sikhkingdoms/1934316" target="_blank" rel="noopener noreferrer" className="border-2 border-amber-400/50 hover:bg-amber-400 hover:text-black text-white px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-735">
              View All Ticket Options
            </motion.a>
          </div>
        </motion.div>
        {/* Corporate Events Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-4xl mx-auto mt-20" data-id="element-736">
          <div className="border-2 border-amber-400/40 p-8 md:p-12 bg-gradient-to-br from-amber-950/20 to-black/60" data-id="element-737">
            <div className="text-center" data-id="element-738">
              <h3 className="font-serif text-3xl text-amber-400 mb-4" data-id="element-739">
                Private Corporate Events
              </h3>
              <div className="w-16 h-px bg-amber-400/30 mx-auto mb-6" data-id="element-740"></div>
              <p className="text-white/80 text-lg mb-6 font-light leading-relaxed" data-id="element-741">
                Transform your next corporate gathering into an unforgettable
                cultural experience. Host your team, clients, or partners for an
                exclusive private evening in the Sikh Kingdoms.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 text-left" data-id="element-742">
                <div className="border-l-2 border-amber-400/30 pl-4" data-id="element-743">
                  <p className="text-amber-400 text-sm font-light mb-2 tracking-wider uppercase" data-id="element-744">
                    Capacity
                  </p>
                  <p className="text-white/70 text-sm font-light" data-id="element-745">
                    Up to 120 guests
                  </p>
                </div>
                <div className="border-l-2 border-amber-400/30 pl-4" data-id="element-746">
                  <p className="text-amber-400 text-sm font-light mb-2 tracking-wider uppercase" data-id="element-747">
                    Customization
                  </p>
                  <p className="text-white/70 text-sm font-light" data-id="element-748">
                    Tailored experiences available
                  </p>
                </div>
                <div className="border-l-2 border-amber-400/30 pl-4" data-id="element-749">
                  <p className="text-amber-400 text-sm font-light mb-2 tracking-wider uppercase" data-id="element-750">
                    Availability
                  </p>
                  <p className="text-white/70 text-sm font-light" data-id="element-751">
                    Select dates in 2026
                  </p>
                </div>
              </div>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20booking%20a%20private%20corporate%20event%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-block border-2 border-amber-400 hover:bg-amber-400 hover:text-black text-white px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-752">
                Inquire About Corporate Booking
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>;
};